// Browser-side PostHog helpers. Kept separate from `posthog.ts` so that module
// can statically import posthog-node without dragging node:fs/path/readline into
// the client bundle.

/** Logto OIDC claims we map onto the PostHog person. */
interface LogtoUserClaims {
  sub: string;
  name?: string | null;
  email?: string | null;
  username?: string | null;
  picture?: string | null;
  phone_number?: string | null;
  email_verified?: boolean | null;
  phone_number_verified?: boolean | null;
  [key: string]: unknown;
}

/**
 * Login is the only place the portal sees the full OIDC claim set, and it always
 * happens before a user can connect an MCP client — so this is what puts a real
 * name/email/phone on the person that later server-side and MCP events attach
 * to by `sub`. Anything not set here stays bare on the profile forever.
 */
function buildPersonProperties(user: LogtoUserClaims): Record<string, unknown> {
  const properties: Record<string, unknown> = {};

  // Null/undefined are skipped rather than sent. PostHog treats an explicit
  // null as "overwrite with null", so forwarding an absent claim would wipe a
  // value another surface had already filled in.
  const assign = (key: string, value: unknown) => {
    if (value !== null && value !== undefined && value !== "") {
      properties[key] = value;
    }
  };

  assign("email", user.email);
  assign("name", user.name);
  // PostHog's person convention is `phone`; Logto emits the OIDC claim name.
  assign("phone", user.phone_number);
  assign("username", user.username);
  assign("avatar", user.picture);
  assign("email_verified", user.email_verified);
  assign("phone_verified", user.phone_number_verified);

  return properties;
}

/**
 * Changes whenever the values we'd write change, so an account that gains a
 * phone number (or was identified by an older, barer version of this function)
 * gets re-identified instead of being skipped by the dedupe below.
 */
function identityFingerprint(
  sub: string,
  properties: Record<string, unknown>,
): string {
  return `${sub}:${Object.entries(properties)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join("|")}`;
}

// Client-side PostHog identification with duplicate prevention
export function identifyUserClientSide(
  posthog: any,
  user: LogtoUserClaims,
): void {
  if (typeof window === 'undefined') {
    return; // Server-side, skip
  }

  const properties = buildPersonProperties(user);
  const fingerprint = identityFingerprint(user.sub, properties);

  // Skip only when the exact same identity was already sent this session.
  if (sessionStorage.getItem('posthog_identified_user') === fingerprint) {
    return;
  }

  // Logto user ID as the stable identifier — the same `sub` the server and MCP
  // paths use as their distinct id, so all three land on one person.
  posthog.identify(user.sub, properties);

  sessionStorage.setItem('posthog_identified_user', fingerprint);
}

// Clear PostHog identification from session storage
export function clearPostHogIdentification(): void {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.removeItem('posthog_identified_user');
  }
}
